import { styled } from "@mui/material/styles";
import { DataGrid } from "../../../components/DataGrid";
import { useGridFeed } from "../hooks/useGridFeed";
import { useGridPagination } from "../hooks/useGridPagination";
import {
  RealEstateReceptionBookDataGridColumns,
  freeUserRealEstateReceptionBookDataGridColumns,
  makeRealEstateReceptionBookDataGridColumns,
} from "../utils";
import { CustomToolbar } from "./CustomToolbar";
import { type IFilterCondition, type IMultipleBookAcquireData } from "../types";
import type { ISingleBookAcquireDialogData } from "@/types/singleBookAcquireDialog";
import React from "react";
import { type GridApiCommunity } from "@mui/x-data-grid-pro/internals";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { type User } from "@/types/localstorage";
import { MAX_FEED_CITY_COUNT_WITH_TRADE } from "../constants/searchLimits";
import { useFeatureFlags } from "@/configs/featureFlag";

interface IProps {
  apiRef: React.MutableRefObject<GridApiCommunity>;
  filterCondition: IFilterCondition;
  toolbarProps: {
    hasTradeState: {
      setHasTrade: React.Dispatch<React.SetStateAction<boolean>>;
      hasTrade: boolean;
    };
    handleSearch: () => void;
  };
  setSingleBookAcquireDialogStatus: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  setSingleBookAcquireDialogData: React.Dispatch<
    React.SetStateAction<ISingleBookAcquireDialogData>
  >;
  multipleBookAcquireData: IMultipleBookAcquireData;
  setMultipleBookAcquireData: React.Dispatch<
    React.SetStateAction<IMultipleBookAcquireData>
  >;
  clearSelection: boolean;
  setClearSelection: React.Dispatch<React.SetStateAction<boolean>>;
}

const StyledDataGrid = styled(DataGrid)`
  border: 0;

  // テーブルヘッダーの背景色を設定
  .MuiDataGrid-columnHeaders {
    line-height: 1.5em !important;
  }

  // テーブルタイトルを太字に
  .MuiDataGrid-columnHeaderTitle {
    font-weight: 700;
  }

  // ページネーションの件数表示を太字に
  .MuiTablePagination-displayedRows {
    font-weight: 700;
  }

  .MuiTablePagination-selectIcon {
    color: #000;
  }
`;

export const FeedGrid: React.FC<IProps> = (props) => {
  const {
    filterCondition,
    toolbarProps,
    setSingleBookAcquireDialogStatus,
    setSingleBookAcquireDialogData,
    multipleBookAcquireData,
    setMultipleBookAcquireData,
    clearSelection,
    setClearSelection,
  } = props;
  const { page, pageSize, changePage, changePageSize } = useGridPagination();
  const { rows, allCount, loading, sortByGridSortModel, selectionModelChange } =
    useGridFeed({
      page,
      pageSize,
      setMultipleBookAcquireData,
      ...filterCondition,
    });

  const [user] = useLocalStorage<User | undefined>("user", undefined);
  const { hotelSearch, dormitorySearch } = useFeatureFlags();
  const rowsPerPageOptions: number[] = [10, 50, 100];

  // エクスポートスイッチの有効/無効を決める関数
  const isItemLoadedAndSelectedProperly = (): boolean => {
    // loadingとallCountとselectedItemの三値でUIの有効/無効を決める
    // loading | allCount >= 1000 | selectedItem > 0 | UIの有効/無効
    //       F |                F |                F | 無効
    //       F |                F |                T | "有効"
    //       F |                T |                F | 無効
    //       F |                T |                T | 無効
    //       T |                F |                F | 無効
    //       T |                F |                T | 無効
    //       T |                T |                F | 無効
    //       T |                T |                T | 無効

    return (
      !loading &&
      !(allCount >= 1000) &&
      multipleBookAcquireData.selectedItem.length > 0
    );
  };

  const isTradeScreeningCityLimitExceeded =
    filterCondition.cities.length > MAX_FEED_CITY_COUNT_WITH_TRADE;

  const getDisabledScreeningMessage = (): string | undefined => {
    if (allCount >= 1000) {
      return "受付帳の検索結果を1,000件未満に絞り込んでください";
    }
    if (isTradeScreeningCityLimitExceeded) {
      return `売買スクリーニング利用時は市区町村を${MAX_FEED_CITY_COUNT_WITH_TRADE}件まで選択してください`;
    }
    return undefined;
  };

  // 売買スクリーニングの有効/無効を決める関数
  const isItemLoadedProperly = (): boolean => {
    return (
      !loading && !(allCount >= 1000) && !isTradeScreeningCityLimitExceeded
    );
  };

  return (
    <StyledDataGrid
      // apiRef={apiRef}
      // 方向性が決まるまでフィルタ機能は無効化
      // https://www.notion.so/trustart/1cb84cb89e4340fe8458d1f34146dc47?pvs=4#2e58a1a7e0654761bbb60c11f32237f5
      disableColumnFilter
      checkboxSelection
      keepNonExistentRowsSelected
      stickyHeader={true}
      rows={rows}
      columns={(!user?.isFree
        ? makeRealEstateReceptionBookDataGridColumns(
            setSingleBookAcquireDialogStatus,
            setSingleBookAcquireDialogData,
            !toolbarProps.hasTradeState.hasTrade
          )
        : freeUserRealEstateReceptionBookDataGridColumns(
            setSingleBookAcquireDialogStatus,
            setSingleBookAcquireDialogData,
            !toolbarProps.hasTradeState.hasTrade
          )
      ).filter((col) => {
        if (
          !hotelSearch &&
          col.field === RealEstateReceptionBookDataGridColumns.IsHotel.field
        ) {
          return false;
        }
        if (
          !dormitorySearch &&
          col.field === RealEstateReceptionBookDataGridColumns.IsDormitory.field
        ) {
          return false;
        }
        return true;
      })}
      initialState={{
        columns: {
          columnVisibilityModel: {
            // Hide columns, the other columns will remain visible
            id: false, // Only CSV
            receptionReasonType: false, // Only CSV
            volumeRate: false, // Only CSV
            hasTrade: false, // 売買スクリーニング列はデフォルトで非表示
          },
        },
      }}
      rowCount={allCount}
      pageSize={pageSize}
      page={page}
      loading={loading}
      sortingMode="server"
      rowsPerPageOptions={rowsPerPageOptions}
      onPageChange={changePage}
      onPageSizeChange={changePageSize}
      onSortModelChange={sortByGridSortModel}
      onSelectionModelChange={selectionModelChange}
      disableSelectionOnClick={true}
      headerHeight={100}
      getRowHeight={() => "auto" as const}
      components={{
        Toolbar: CustomToolbar,
      }}
      componentsProps={{
        toolbar: {
          disabledExportBtn: !isItemLoadedAndSelectedProperly(),
          disabledScreening: !isItemLoadedProperly(),
          disabledScreeningMessage: getDisabledScreeningMessage(),
          hasTradeState: toolbarProps.hasTradeState,
          handleSearch: toolbarProps.handleSearch,
          user,
          filterCondition,
          page,
          pageSize,
          allCount,
          rowsPerPageOptions,
          changePage,
          changePageSize,
          clearSelection,
          setClearSelection,
        },
      }}
    />
  );
};
